import { TypeIcon } from "../common/TypeIcon";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useEntities } from "../../hooks/useEntities";
import { useInteractions } from "../../hooks/useInteractions";
import { EntityPicker } from "../entities/EntityPicker";
import { linkInteractionsToEntity } from "../../utils/entityLinking";
import {
  nameSimilarity,
  rankByName,
  RELATED_THRESHOLD,
  MATCH_STRONG,
} from "../../utils/entityMatching";
import { entityTypeLabel } from "../../utils/tripDisplay";
import { ENTITY_TYPES, getInteractionEntityType } from "../../types";
import "./LinkPlacePanel.css";

interface LinkPlacePanelProps {
  interactionId: string;
  tripId: string;
  /** What the activity was logged as — the name we're matching on. */
  name: string | null;
  customResponses: Record<string, unknown> | null | undefined;
  onClose: () => void;
}

/**
 * Give an activity that was logged as free text a real place.
 *
 * Mounted only while the panel is open, because it loads the whole entity
 * catalog and the trip's other activities to do its matching — work that would
 * be wasted on every activity detail view otherwise.
 *
 * The second half is the part that matters for a trip that was written up
 * afterwards: the same place usually got typed in several times, so the panel
 * offers to link every activity in this trip that looks like this one, not
 * just the one you happen to be looking at.
 */
export function LinkPlacePanel({
  interactionId,
  tripId,
  name,
  customResponses,
  onClose,
}: LinkPlacePanelProps) {
  const { entities, loading: entitiesLoading } = useEntities();
  const { interactions } = useInteractions(tripId);
  const [alsoLinkSimilar, setAlsoLinkSimilar] = useState(true);

  const matches = useMemo(
    () => rankByName(name, entities, (e) => e.displayName, { limit: 4 }),
    [name, entities],
  );

  const similar = useMemo(
    () =>
      interactions.filter(
        (i) =>
          i.id !== interactionId &&
          !i.entityId &&
          nameSimilarity(i.name, name) >= RELATED_THRESHOLD,
      ),
    [interactions, interactionId, name],
  );

  const targets = alsoLinkSimilar
    ? [interactionId, ...similar.map((i) => i.id)]
    : [interactionId];

  const link = (entityId: string) => {
    linkInteractionsToEntity(targets, entityId);
    onClose();
  };

  const activityType = getInteractionEntityType(null, customResponses);
  const createParams = new URLSearchParams({
    name: name ?? "",
    linkInteractions: targets.join(","),
    returnTo: `/trips/${tripId}/interactions/${interactionId}`,
  });
  if (activityType) createParams.set("type", activityType);

  return (
    <div className="link-place">
      <div className="link-place-head">
        <h3>Link to a place</h3>
        <button type="button" className="btn-link" onClick={onClose}>Close</button>
      </div>

      {similar.length > 0 && (
        <label className="link-place-also">
          <input
            type="checkbox"
            checked={alsoLinkSimilar}
            onChange={(e) => setAlsoLinkSimilar(e.target.checked)}
          />
          <span>
            Also link {similar.length} other{" "}
            {similar.length === 1 ? "activity" : "activities"} on this trip logged as{" "}
            {[...new Set(similar.map((i) => i.name).filter(Boolean))]
              .slice(0, 3)
              .map((n) => `“${n}”`)
              .join(", ")}
          </span>
        </label>
      )}

      {entitiesLoading ? (
        <p className="text-muted link-place-empty">Loading places...</p>
      ) : matches.length > 0 ? (
        <ul className="link-place-matches">
          {matches.map((match) => (
            <li key={match.item.id}>
              <button type="button" className="link-place-match" onClick={() => link(match.item.id)}>
                <span><TypeIcon type={match.item.type} name={match.item.displayName} /> {match.item.displayName}</span>
                <span className={`link-place-score${match.score >= MATCH_STRONG ? " strong" : ""}`}>
                  {entityTypeLabel(match.item.type)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-muted link-place-empty">
          Nothing in the catalog looks like “{name || "this activity"}”.
        </p>
      )}

      <div className="link-place-picker">
        <span className="form-label">Or pick any place</span>
        <EntityPicker
          types={[...ENTITY_TYPES]}
          value={null}
          placeholder="Search the catalog..."
          onChange={(entityId) => {
            if (entityId) link(entityId);
          }}
        />
      </div>

      <Link to={`/entities/new?${createParams.toString()}`} className="btn btn-small btn-secondary">
        Create a place from this activity
      </Link>
    </div>
  );
}
